import { type TieredPriceProductId, calculateUsageCost, TIERED_PRODUCTS } from "@project/common";
import { AgGridTable } from "../../../../components/AgGridTable.js";
import { useMemo, useCallback } from "react";
import { useProductNames } from "../../components/hooks/productLabels.js";
import type { ProductUsageSums } from "../../components/utils/billingAggregators.js";
import { FormattedMessage, useIntl } from "react-intl";
import { formatUSD } from "../../components/utils/costDisplay.js";
import { Box, Skeleton } from "@mantine/core";
import { ErrorAlert } from "../../components/ErrorAlert.js";
import type { ColDef } from "ag-grid-community";

interface CombinedSpendTableProps {
  metrics: TieredPriceProductId[];
  usageSums: ProductUsageSums;
  isLoadingUsageSums: boolean;
  isErrorUsageSums: boolean;
}

interface Row {
  metric: string;
  usage: number;
  freeUsage: number;
  billedUsage: number;
  unitPrice: string;
  spend: string;
}

export function CombinedSpendTable({
  metrics,
  usageSums,
  isLoadingUsageSums,
  isErrorUsageSums,
}: CombinedSpendTableProps) {
  const intl = useIntl();
  const getName = useProductNames();

  const generateRow = useCallback((metric: TieredPriceProductId): Row => {
    const usage = usageSums[metric];
    const { unitSize, freeTierAllowance } = TIERED_PRODUCTS[metric];
    const meteredUsage = Math.ceil(usage / unitSize);
    const freeUsage = Math.min(meteredUsage, freeTierAllowance * unitSize);
    const unitPrice = Number(TIERED_PRODUCTS[metric].price) / 100;
    const spend = calculateUsageCost(usage, metric);

    return {
      metric: getName(metric),
      usage: meteredUsage,
      freeUsage,
      billedUsage: meteredUsage - freeUsage,
      unitPrice: formatUSD(intl, unitPrice, false),
      spend: formatUSD(intl, spend),
    };
  }, [usageSums, getName, intl]);

  const cols = useMemo((): ColDef<Row>[] => [
    {
      field: "metric",
      headerName: intl.formatMessage({ defaultMessage: "Metric", id: "col.metric" }),
    },
    {
      field: "usage",
      headerName: intl.formatMessage({ defaultMessage: "Usage", id: "cols.usage" }),
    },
    {
      field: "freeUsage",
      headerName: intl.formatMessage({ defaultMessage: "Free usage", id: "cols.freeUsage" }),
    },
    {
      field: "billedUsage",
      headerName: intl.formatMessage({ defaultMessage: "Billed usage", id: "cols.billedUsage" }),
    },
    {
      field: "unitPrice",
      headerName: intl.formatMessage({ defaultMessage: "Unit price", id: "cols.unitPrice" }),
    },
    {
      field: "spend",
      headerName: intl.formatMessage({ defaultMessage: "Spend", id: "label.spend" }),
    },
  ], [intl]);

  const rows = useMemo(() => metrics.map(generateRow), [metrics, generateRow]);

  const pinnedBottomRowData = useMemo((): Partial<Row>[] => {
    const totalSpend = metrics.reduce((subTotal, metric) =>
      subTotal + calculateUsageCost(usageSums[metric], metric), 0);

    return [{
      metric: intl.formatMessage({ defaultMessage: "Total", id: "footer.total" }),
      spend: formatUSD(intl, totalSpend),
    }];
  }, [metrics, intl, usageSums]);

  if (isLoadingUsageSums) {
    return (
      <Box>
        {Array.from({ length: metrics.length }).map((_, i) => (
          <Skeleton h="xl" my="xs" key={i} />
        ))}
      </Box>
    );
  }
  else if (isErrorUsageSums) {
    return (
      <ErrorAlert>
        <FormattedMessage
          defaultMessage="Error creating combined spend table"
          id="combinedSpend.error"
        />
      </ErrorAlert>
    );
  }
  else {
    return (
      <AgGridTable
        rowData={rows}
        columnDefs={cols}
        pinnedBottomRowData={pinnedBottomRowData}
        domLayout="autoHeight"
      />
    );
  }
}
